import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Calendar, Clock } from 'lucide-react';

const BlogNutritionDiabetes = () => {
  return (
    <div className="bg-white">

      {/* HERO */}
      <section className="relative h-[460px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=1400&q=80"
          alt="Nutrition for Diabetes"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 flex items-end pb-12 px-8 md:px-20">
          <div className="max-w-3xl">
            <span className="inline-block bg-green-500 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4">NUTRITION</span>
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight drop-shadow-lg mb-4">
              What to Eat When You Have Diabetes: A Practical Nutrition Guide
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <span className="flex items-center gap-1.5"><Calendar size={14} />2 Apr 2024</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><Clock size={14} />7 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* BREADCRUMB */}
      <div className="bg-gray-100 py-3.5">
        <div className="max-w-7xl mx-auto px-6 text-sm flex items-center gap-2">
          <Link to="/" className="text-gray-500 hover:text-[#39a5d3]">Home</Link>
          <span className="text-gray-400">/</span>
          <Link to="/blog" className="text-gray-500 hover:text-[#39a5d3]">Blogs</Link>
          <span className="text-gray-400">/</span>
          <span className="text-[#39a5d3] font-semibold">What to Eat When You Have Diabetes</span>
        </div>
      </div>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

            <div className="lg:col-span-2 text-gray-700 leading-relaxed">

              <p className="text-lg leading-relaxed mb-8 border-l-4 border-green-500 pl-5 bg-green-50 py-4 pr-4 rounded-r-lg">
                Managing blood sugar through diet is not about deprivation — it is about understanding which foods raise blood sugar quickly, which provide slow sustained energy, and how to build meals that keep glucose levels stable throughout the day. With the right knowledge, a diabetic diet can be varied, satisfying, and genuinely enjoyable. This guide explains the nutritional principles behind blood sugar management and translates them into practical everyday food choices for the Indian context.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-green-500/25">Understanding Blood Sugar and Food</h2>
              <p className="mb-4">When you eat carbohydrates, your digestive system breaks them down into glucose, which enters the bloodstream and raises blood sugar levels. In a person without diabetes, the pancreas releases insulin to move this glucose into cells for energy. In Type 2 diabetes, cells become resistant to insulin, and the pancreas cannot compensate sufficiently — blood sugar rises and stays elevated, causing the range of complications associated with the condition.</p>
              <p className="mb-8">Not all carbohydrates raise blood sugar at the same rate. The glycaemic index (GI) measures how quickly a food raises blood glucose compared to pure glucose. Low GI foods — most vegetables, legumes, whole grains — cause a slow, gradual rise. High GI foods — white rice, white bread, sugary drinks, potatoes — cause a rapid spike followed by a crash. Understanding this distinction is the foundation of effective diabetic nutrition.</p>

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-green-500/25">Foods to Eat More Of</h2>
              <div className="space-y-3 mb-10">
                {[
                  { t: 'Non-Starchy Vegetables', d: 'Vegetables grown above ground are generally low in carbohydrates and high in fibre, vitamins, and minerals. Leafy greens (spinach, methi, palak), broccoli, cauliflower, cabbage, capsicum, cucumber, tomatoes, and bitter gourd (karela — particularly beneficial for blood sugar) should form the base of every meal. Aim for at least half your plate to be non-starchy vegetables at each meal.' },
                  { t: 'Legumes and Pulses', d: 'Dal, rajma, chana, moong, lentils, and other pulses are exceptional diabetic foods. They are rich in protein and fibre, have a low glycaemic index, and provide sustained energy without spiking blood sugar. A bowl of dal at each meal is one of the most effective dietary interventions for blood sugar management — and it fits perfectly into traditional Indian cuisine.' },
                  { t: 'Whole Grains Over Refined Grains', d: 'Whole wheat chapati is preferable to maida-based foods. Brown rice has a lower glycaemic index than white rice, though portions still matter. Oats, barley, jowar (sorghum), bajra (pearl millet), and ragi are excellent alternatives to refined grains, providing fibre that slows glucose absorption. Multigrain and bran-enriched options are widely available.' },
                  { t: 'Lean Protein at Every Meal', d: 'Protein does not raise blood sugar and helps you feel full, reducing the likelihood of overeating carbohydrates. Include eggs, low-fat paneer, curd (unsweetened), fish, chicken (without skin), or legumes at every meal. A protein-rich breakfast is particularly important — it sets your blood sugar trajectory for the entire morning.' },
                  { t: 'Healthy Fats', d: 'Nuts (almonds, walnuts, peanuts in moderation), seeds (flaxseed, chia), avocado, and olive oil slow digestion and reduce the glycaemic impact of meals. They also support heart health, which is critical given that diabetes significantly increases cardiovascular risk. Replace refined vegetable oils with mustard oil, groundnut oil, or limited amounts of ghee.' },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="font-bold text-gray-900 mb-1.5">{item.t}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.d}</p>
                  </div>
                ))}
              </div>

              {/* Mid article image */}
              <div className="my-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                  <img
                    src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=800&q=80"
                    alt="Healthy balanced meal"
                    className="w-full h-64 object-cover"
                  />
                  <div className="bg-gray-50 px-4 py-2.5 border-t border-gray-100">
                    <p className="text-xs text-gray-500 italic text-center">A balanced plate — half vegetables, a quarter protein, a quarter whole grain — is the foundation of diabetic nutrition.</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">The Plate Method — A Simple Framework</h3>
                  <p className="text-sm leading-relaxed mb-3">Rather than counting every gram of carbohydrate, the plate method provides a practical visual framework for building balanced meals without complex calculations.</p>
                  <ul className="space-y-2 mb-3">
                    {[
                      'Half your plate: non-starchy vegetables',
                      'One quarter: lean protein (dal, paneer, egg, fish, chicken)',
                      'One quarter: complex carbohydrates (chapati, brown rice, oats)',
                      'Add a small portion of healthy fat',
                      'Include a glass of water or buttermilk — not juice or sugary drinks',
                    ].map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-gray-600">This method works for most meals without requiring food scales, calorie apps, or complex calculations.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-green-500/25">Foods to Limit or Avoid</h2>
              <div className="space-y-4 mb-10">
                {[
                  { n: '01', t: 'Sugary Drinks and Fruit Juices', d: 'This is the single most impactful dietary change for most diabetics. Sugary tea and coffee, cold drinks, packaged fruit juices, and energy drinks cause rapid blood sugar spikes with no nutritional benefit. Even natural fruit juice — without the fibre of the whole fruit — raises blood sugar quickly. Switch to water, plain buttermilk, unsweetened chaas, or black or green tea.' },
                  { n: '02', t: 'White Rice in Large Portions', d: 'White rice has a high glycaemic index and is a staple of the Indian diet — which partly explains the high rates of diabetes in South India. This does not mean eliminating rice entirely. Reduce portion size significantly (a quarter of the plate maximum), choose parboiled or basmati rice over polished white rice, and always combine rice with dal and vegetables to slow absorption.' },
                  { n: '03', t: 'Refined Flour (Maida) Products', d: 'White bread, naan, paratha made with maida, biscuits, namkeen, and packaged snacks made from refined flour spike blood sugar rapidly. Replace with whole wheat alternatives or choose snacks based on nuts, seeds, or roasted chana.' },
                  { n: '04', t: 'Sweets and Mithai', d: 'Traditional Indian sweets — ladoo, barfi, halwa, kheer — are extremely high in sugar and refined carbohydrates. They should be reserved for rare special occasions, eaten in very small amounts, and always combined with protein. Sugar-free alternatives made with artificial sweeteners can be used occasionally but should not become a daily habit.' },
                  { n: '05', t: 'Fried Foods', d: 'Samosas, pakoras, puri, bhatura, and other deep-fried foods combine refined carbohydrates with large amounts of unhealthy fat, creating a particularly poor combination for blood sugar management. Limit to rare occasions.' },
                ].map(item => (
                  <div key={item.n} className="flex gap-4 bg-white border border-gray-200 rounded-lg p-5">
                    <div className="bg-red-500 text-white font-bold text-sm w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0">{item.n}</div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{item.t}</h3>
                      <p className="text-gray-700 text-sm leading-relaxed">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-green-50 border border-green-100 rounded-2xl p-7 mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-3">Practical Tips for Daily Indian Meals</h2>
                <ul className="space-y-2">
                  {[
                    'Start lunch and dinner with a bowl of salad or sabzi — the fibre slows subsequent carbohydrate absorption',
                    'Eat chapati and rice together rather than large portions of either alone',
                    'Add a handful of methi seeds soaked overnight to your morning routine — evidence supports blood sugar benefits',
                    'Choose whole fruit over fruit juice — the fibre makes a significant difference',
                    'Eat at regular intervals — skipping meals leads to overeating and blood sugar swings',
                    'Walk for 10 to 15 minutes after meals — post-meal walking significantly reduces blood sugar spikes',
                    'Read labels on packaged foods — many seemingly healthy products contain significant added sugar',
                    'Work with a registered dietitian for a personalised meal plan based on your medications and blood sugar patterns',
                  ].map((p, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 flex items-center justify-between">
                <Link to="/blog" className="inline-flex items-center gap-2 text-[#39a5d3] font-semibold text-sm hover:underline">
                  <ChevronLeft size={16} /> Back to All Blogs
                </Link>
               
              </div>
            </div>

            {/* Sidebar */}
            <aside className="space-y-7">
              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="bg-[#19354a] px-5 py-4 rounded-t-xl">
                  <h3 className="text-white font-bold text-sm">Key Facts</h3>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { l: 'Indians with diabetes', v: '101 million+' },
                    { l: 'Diet impact on HbA1c', v: 'Up to 2% reduction' },
                    { l: 'Post-meal walk benefit', v: 'Significant' },
                    { l: 'Low GI foods', v: 'GI below 55' },
                  ].map((f, i) => (
                    <div key={i} className="flex justify-between border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-xs text-gray-500">{f.l}</p>
                      <p className="text-xs font-bold text-[#39a5d3]">{f.v}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="bg-[#19354a] px-5 py-4 rounded-t-xl">
                  <h3 className="text-white font-bold text-sm">Low GI Indian Foods</h3>
                </div>
                <div className="p-5">
                  {['Moong dal', 'Chana (whole)', 'Rajma', 'Oats', 'Barley', 'Ragi', 'Bitter gourd', 'Most vegetables', 'Curd (unsweetened)', 'Nuts and seeds'].map((f, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                      <p className="text-xs text-gray-600">{f}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#19354a] rounded-xl p-6 text-white text-center">
                <h3 className="font-bold mb-2">Consult a Specialist</h3>
                <p className="text-white/80 text-xs mb-4">Find verified diabetes and nutrition specialists on Docmize.</p>
                <Link to="/doctors" className="block bg-white text-[#19354a] font-bold py-2.5 rounded-lg text-sm hover:bg-gray-100 transition-colors">
                  Find a Doctor
                </Link>
                <div className="mt-4 pt-3 border-t border-white/20">
                  <p className="text-white/70 text-xs">Call us anytime</p>
                  <a href="tel:+918920845297" className="text-white font-bold text-sm">+91 89208 45297</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogNutritionDiabetes;